import { photoUrl } from "../lib/api";
import { Link } from "react-router-dom";
import { Badge } from "./ui/badge";
import { MapPin, Bed, Bath, CheckCircle, Shield } from "lucide-react";

const typeLabels = { room: "Room", apartment: "Apartment", house: "House" };
const leaseLabels = { short: "Short-Term", mid: "Mid-Term", long: "Long-Term" };

const defaultImages = {
  room: "https://images.unsplash.com/photo-1662454419716-c4c504728811?w=600",
  apartment: "https://images.unsplash.com/photo-1666282167632-c613fbeb163c?w=600",
  house: "https://images.pexels.com/photos/279719/pexels-photo-279719.jpeg?w=600",
};

export default function PropertyCard({ listing, matchScore }) {
  const photo = photoUrl(listing.photos?.[0]) || defaultImages[listing.property_type] || defaultImages.apartment;
  const isExternal = photo.startsWith("http");

  return (
    <Link
      to={`/listing/${listing.listing_id}`}
      data-testid={`property-card-${listing.listing_id}`}
      className="group block bg-white rounded-2xl overflow-hidden border border-[#d5d3ce]/60 shadow-[0_8px_32px_rgba(0,0,0,0.06)] hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(0,0,0,0.1)] transition-all duration-300"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={isExternal ? photo : undefined}
          alt={listing.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.target.src = defaultImages[listing.property_type] || defaultImages.apartment; }}
        />
        <div className="absolute top-3 left-3 flex gap-2">
          {listing.verified && (
            <Badge className="bg-[#1a6b3c] text-white border-0 rounded-full text-xs font-medium px-2.5 py-1 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Verified
            </Badge>
          )}
          {listing.bah_eligible && (
            <Badge className="bg-[#0d0d0d] text-white border-0 rounded-full text-xs font-medium px-2.5 py-1 flex items-center gap-1">
              <Shield className="w-3 h-3" /> BAH
            </Badge>
          )}
        </div>
        {matchScore !== undefined && (
          <div className="absolute top-3 right-3 bg-[#c8f563] text-[#0d0d0d] rounded-full px-3 py-1 text-sm font-bold">
            {matchScore}% Match
          </div>
        )}
        <div className="absolute bottom-3 left-3">
          <Badge className="bg-white/90 text-[#0d0d0d] border-0 rounded-full text-xs font-medium px-2.5 py-1">
            {typeLabels[listing.property_type] || listing.property_type}
          </Badge>
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-[#0d0d0d] text-lg leading-tight line-clamp-1 font-['Fraunces']">
            {listing.title}
          </h3>
          <p className="text-lg font-bold text-[#1a6b3c] whitespace-nowrap ml-2">
            ${listing.price?.toLocaleString()}<span className="text-xs font-normal text-[#5a5a5a]">/mo</span>
          </p>
        </div>

        <div className="flex items-center gap-1 text-[#5a5a5a] text-sm mb-3">
          <MapPin className="w-3.5 h-3.5" />
          <span>{listing.location}, {listing.city}</span>
        </div>

        <div className="flex items-center gap-4 text-sm text-[#5a5a5a]">
          <span className="flex items-center gap-1"><Bed className="w-4 h-4" /> {listing.bedrooms} bed</span>
          <span className="flex items-center gap-1"><Bath className="w-4 h-4" /> {listing.bathrooms} bath</span>
          {listing.sqft && <span>{listing.sqft} sqft</span>}
        </div>

        <div className="mt-3 pt-3 border-t border-[#e5e3de] flex items-center justify-between">
          <span className="text-xs font-medium text-[#5a5a5a] bg-[#faf8f3] rounded-full px-2.5 py-1">
            {leaseLabels[listing.lease_type] || listing.lease_type}
          </span>
          {listing.pet_friendly && (
            <span className="text-xs text-[#1a6b3c] font-medium">Pet Friendly</span>
          )}
        </div>
      </div>
    </Link>
  );
}
